// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search, User, Mail, Phone, Calendar, DollarSign } from 'lucide-react';
import { format } from 'date-fns';

interface Registration {
  id: string;
  registration_type: string;
  registrant_data: any;
  payment_status: string;
  status: string;
  created_at: string;
  pricing_breakdown: any;
}

const StaffRegistrations: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [eventName, setEventName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    checkPermissionAndFetchData();
  }, [eventId, profile?.email]);

  const checkPermissionAndFetchData = async () => {
    if (!eventId || !profile?.email) {
      setIsLoading(false);
      return;
    }

    try {
      // Check if staff has permission to view registrations for this event
      const { data: staffRecord } = await supabase
        .from('event_staff')
        .select('id')
        .eq('email', profile.email)
        .eq('is_active', true)
        .maybeSingle();

      if (!staffRecord) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      const { data: assignment } = await supabase
        .from('event_staff_assignments')
        .select('permissions')
        .eq('staff_id', staffRecord.id)
        .eq('event_id', eventId)
        .maybeSingle();

      if (!assignment) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      const permissions = assignment.permissions || [];
      if (!permissions.includes('all') && !permissions.includes('view_registrations')) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      setHasPermission(true);

      // Fetch event name
      const { data: eventData } = await supabase
        .from('events')
        .select('name')
        .eq('id', eventId)
        .single();

      if (eventData) {
        setEventName(eventData.name);
      }

      // Fetch registrations
      const { data: registrationData, error } = await supabase
        .from('registrations')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching registrations:', error);
      } else {
        setRegistrations(registrationData || []);
      }
    } catch (error) {
      console.error('Error in checkPermissionAndFetchData:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const data = reg.registrant_data || {};
    
    return (
      data.team_name?.toLowerCase().includes(searchLower) ||
      data.coach_name?.toLowerCase().includes(searchLower) ||
      data.coach_email?.toLowerCase().includes(searchLower) ||
      data.athlete_name?.toLowerCase().includes(searchLower) ||
      data.email?.toLowerCase().includes(searchLower) ||
      data.name?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status: string, paymentStatus: string) => {
    if (paymentStatus === 'paid') {
      return <Badge variant="success">Paid</Badge>;
    }
    if (paymentStatus === 'pending') {
      return <Badge variant="secondary">Pending Payment</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="destructive">Cancelled</Badge>;
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const getRegistrantInfo = (registration: Registration) => {
    const data = registration.registrant_data || {};
    const pricing = registration.pricing_breakdown || {};
    
    // Extract division/age group from multiple possible locations
    const division = data.team?.age_group || 
                    data.team?.division || 
                    data.division ||
                    pricing.division || '';
    
    if (registration.registration_type === 'team') {
      return {
        name: data.team_name || data.team?.name || 'Unknown Team',
        division,
        subtitle: data.coach_name || data.coach?.name || '',
        email: data.coach_email || data.coach?.email || '',
        phone: data.coach_phone || data.coach?.phone || ''
      };
    } else {
      return {
        name: data.athlete_name || data.name || 'Unknown',
        division,
        subtitle: '',
        email: data.email || '',
        phone: data.phone || ''
      };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Loading registrations...</p>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-muted-foreground mb-4">
              You don't have permission to view registrations for this event.
            </p>
            <Button onClick={() => navigate('/staff/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/staff/dashboard')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">{eventName}</h1>
          <p className="text-muted-foreground">Viewing {filteredRegistrations.length} registrations</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or team..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Registrations List */}
      <div className="space-y-4">
        {filteredRegistrations.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">
                {searchTerm ? 'No registrations match your search.' : 'No registrations found for this event.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredRegistrations.map((registration) => {
            const info = getRegistrantInfo(registration);
            const pricing = registration.pricing_breakdown || {};
            
            return (
              <Card key={registration.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold">
                          {info.name}
                          {info.division && (
                            <span className="text-muted-foreground font-normal"> • {info.division}</span>
                          )}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {registration.registration_type}
                        </Badge>
                        {getStatusBadge(registration.status, registration.payment_status)}
                      </div>
                      {info.subtitle && (
                        <p className="text-sm text-muted-foreground">{info.subtitle}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary">
                        ${((pricing.total || 0) / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {info.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{info.email}</span>
                      </div>
                    )}
                    {info.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{info.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {format(new Date(registration.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StaffRegistrations;
